// src/Payroll_Management/PayRoll_Report/PerformanceReportViewPrint.jsx
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const BASE_URL = "http://nissi-india.com";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function PerformanceReportViewPrint() {
  const q = useQuery();

  const finYearId = localStorage.getItem("finYearId") || "";
  const performId = q.get("performId") || "";
  const autoPrint = (q.get("autoPrint") || "1") === "1";

  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDetails = async () => {
    if (!String(finYearId).trim() || !String(performId).trim()) return;

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-report/performance-view`, {
        params: { finYearId: Number(finYearId), performId: Number(performId) },
      });

      if (res?.data?.success) setRow(res.data.row || null);
      else setRow(null);
    } catch {
      setRow(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performId]);

  useEffect(() => {
    if (!autoPrint) return;
    if (!row) return;
    const t = setTimeout(() => window.print(), 250);
    return () => clearTimeout(t);
  }, [row, autoPrint]);

  // ✅ change these to match your company print header
  const company = {
    name: "Nissi Infotech",
    sub: "Nissi",
    line1: "CIVIL CONSTRUCTION",
    addr: "3/42-12, Nagappa colony , kavundampalayam - Coimbatore - Tamil Nadu.",
    phone: "Phone No : 9003744644",
    email: "infraconsoft-implementation",
  };

  // ✅ if you have a real logo URL, replace below with your served asset
  // Example: const logoUrl = "/General/Theme1/Images/CompanyLogo.PNG";
  const logoUrl = "/General/Theme1/Images/CompanyLogo.PNG";

  const v = (x) => (x === null || x === undefined ? "" : String(x));

  return (
    <>
      <style>{`
        /* page */
        * { box-sizing: border-box; }
        body { margin: 0; background: #fff; color: #000; font-family: "Times New Roman", Times, serif; }
        .page { width: 1000px; margin: 12px auto; }

        /* table borders like old print */
        table { border-collapse: collapse; width: 100%; }
        .outer { border: 1px solid #000; }
        .outer td { border: 1px solid #000; vertical-align: top; }

        /* header */
        .hdrWrap { padding: 10px; }
        .hdrInner { width: 78%; margin: 0 auto; border: 1px solid #000; }
        .hdrInner td { border: none; }
        .logoBox { width: 210px; padding: 6px; }
        .logoBox img { width: 190px; height: 95px; object-fit: contain; display: block; }
        .hdrText { text-align: center; padding: 6px 10px; line-height: 1.15; }
        .hdrText .cname { font-weight: 700; font-size: 22px; }
        .hdrText .sub { font-size: 14px; margin-top: 2px; }
        .hdrText .line1 { font-weight: 700; font-size: 14px; margin-top: 4px; }
        .hdrText .addr { font-size: 13px; margin-top: 6px; }
        .hdrText .phone { font-size: 13px; margin-top: 2px; }
        .hdrText .email { font-size: 13px; margin-top: 2px; }

        /* title row */
        .titleRow { text-align: center; font-weight: 700; font-size: 14px; padding: 10px 0; }

        /* content table */
        .detail { width: 100%; }
        .detail th, .detail td { border: 1px solid #000; padding: 8px 10px; font-size: 14px; }
        .detail th { font-weight: 700; text-align: left; background: #fff; }
        .detail .lbl { width: 260px; }
        .detail .val { width: auto; }

        .muted { font-size: 14px; padding: 18px; text-align: center; }

        @media print {
          .page { width: auto; margin: 0; }
          @page { size: A4 landscape; margin: 10mm; }
        }
      `}</style>

      <div className="page">
        <table className="outer">
          {/* header block */}
          <tbody>
            <tr>
              <td>
                <div className="hdrWrap">
                  <table className="hdrInner">
                    <tbody>
                      <tr>
                        <td className="logoBox">
                          <img
                            src={logoUrl}
                            alt="Company Logo"
                            onError={(e) => {
                              // fallback: hide broken image
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        </td>
                        <td className="hdrText">
                          <div className="cname">{company.name}</div>
                          <div className="sub">{company.sub}</div>
                          <div className="line1">{company.line1}</div>
                          <div className="addr">{company.addr}</div>
                          <div className="phone">{company.phone}</div>
                          <div className="email">{company.email}</div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>

            {/* title */}
            <tr>
              <td className="titleRow">EMPLOYEE PERFORMANCE REPORT</td>
            </tr>

            {/* body */}
            <tr>
              <td style={{ padding: 0 }}>
                {loading ? (
                  <div className="muted">Loading...</div>
                ) : !row ? (
                  <div className="muted">No Records Found</div>
                ) : (
                  <table className="detail">
                    <tbody>
                      <tr>
                        <th className="lbl">Employee Code</th>
                        <td className="val">: {v(row.fld_Employeecode)}</td>
                      </tr>
                      <tr>
                        <th className="lbl">Employee Name</th>
                        <td className="val">: {v(row.fld_Employee)}</td>
                      </tr>
                      <tr>
                        <th className="lbl">Designation</th>
                        <td className="val">: {v(row.fld_designationname)}</td>
                      </tr>
                      <tr>
                        <th className="lbl">Branch</th>
                        <td className="val">: {v(row.fld_BranchName)}</td>
                      </tr>
                      <tr>
                        <th className="lbl">Date</th>
                        <td className="val">: {v(row.PerformanceDate)}</td>
                      </tr>
                      <tr>
                        <th className="lbl">Title</th>
                        <td className="val">: {v(row.fld_Title)}</td>
                      </tr>
                      <tr>
                        <th className="lbl">Details</th>
                        <td className="val" style={{ whiteSpace: "pre-wrap" }}>
                          : {v(row.fld_Details)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}
